import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Pause, Play, Rocket, RotateCcw } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type { components } from '@/api/schema';
import { Roles } from '@/auth/roles';
import { useCan, useTenantId } from '@/auth/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Card, Skeleton } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { DataTable, EmptyRow, Field, RootError, Section, Switch } from '@/components/ui/form-controls';
import { Input, NativeSelect } from '@/components/ui/input';
import { errorMessage } from '@/api/problem';
import { asArray, asRecord } from '@/lib/json';
import { DeployChecksList } from './DeployChecksList';
import { branchesFromConfig, buildDeployment, canDeploy } from './logic';

type DeploymentDto = components['schemas']['DeploymentDto'];

function statusVariant(s: DeploymentDto['status']): 'success' | 'warning' | 'destructive' | 'default' {
  return s === 'active' ? 'success' : s === 'paused' ? 'warning' : s === 'failed' ? 'destructive' : 'default';
}

export function DeployPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const tenantId = useTenantId();
  const qc = useQueryClient();
  const canManage = useCan({ roles: [Roles.PlatformAdmin, Roles.TenantAdmin] });
  const keys = {
    agent: queryKeys.scoped(tenantId, 'agents', id),
    versions: queryKeys.scoped(tenantId, 'agents', id, 'versions'),
    deployments: queryKeys.scoped(tenantId, 'agents', id, 'deployments'),
  };

  const agent = useQuery({ queryKey: keys.agent, queryFn: async () => unwrap(await api.GET('/v1/agents/{id}', { params: { path: { id } } })) });
  const versions = useQuery({
    queryKey: keys.versions,
    queryFn: async () => unwrap(await api.GET('/v1/agents/{id}/versions', { params: { path: { id } } })),
  });
  const deployments = useQuery({
    queryKey: keys.deployments,
    queryFn: async () => unwrap(await api.GET('/v1/agents/{id}/deployments', { params: { path: { id }, query: { limit: 50 } } })),
  });
  const jdeConfig = useQuery({
    queryKey: queryKeys.scoped(tenantId, 'jde-config'),
    enabled: !!tenantId && canManage,
    retry: false,
    meta: { silent: true },
    queryFn: async () => unwrap(await api.GET('/v1/tenants/{id}/jde-config', { params: { path: { id: tenantId ?? '' } } })) as unknown,
  });

  const published = useMemo(
    () => (versions.data?.items ?? []).filter((v) => v.status === 'published').sort((a, b) => b.version - a.version),
    [versions.data],
  );
  const [version, setVersion] = useState<number | null>(null);
  useEffect(() => {
    if (version === null && published[0]) setVersion(published[0].version);
  }, [published, version]);

  const checks = useQuery({
    queryKey: queryKeys.scoped(tenantId, 'agents', id, 'deploy-checks', version),
    enabled: version !== null && canManage,
    queryFn: async () => unwrap(await api.GET('/v1/agents/{id}/deployments/checks', { params: { path: { id }, query: { version: version ?? 0 } } })),
  });

  const branches = branchesFromConfig(jdeConfig.data);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [startAt, setStartAt] = useState('');
  const [deployError, setDeployError] = useState<string | null>(null);
  const [pauseOpen, setPauseOpen] = useState(false);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.scoped(tenantId, 'agents', id) });
  };

  const deploy = useMutation({
    meta: { silent: true },
    mutationFn: async () =>
      unwrap(await api.POST('/v1/agents/{id}/deployments', { params: { path: { id } }, body: buildDeployment(version ?? 0, selectedBranches, startAt) })),
    onSuccess: async () => {
      setDeployError(null);
      toast.success(t('admin.deploy.deployed', { version }));
      await refresh();
    },
    onError: (err) => setDeployError(errorMessage(err)),
  });
  const rollback = useMutation({
    mutationFn: async (d: string) => unwrap(await api.POST('/v1/agents/{id}/deployments/{d}/rollback', { params: { path: { id, d } } })),
    onSuccess: async (res) => {
      toast.success(res.active ? t('admin.deploy.rolledBackTo', { version: res.active.version }) : t('admin.deploy.rolledBackNone'));
      await refresh();
    },
  });
  const resume = useMutation({
    mutationFn: async () => unwrap(await api.POST('/v1/agents/{id}/resume', { params: { path: { id } } })),
    onSuccess: async () => {
      toast.success(t('admin.deploy.resumed'));
      await refresh();
    },
  });

  const history = deployments.data?.items ?? [];
  const active = history.find((d) => d.status === 'active' || d.status === 'paused');
  const a = agent.data;

  return (
    <div>
      <PageHeader help="deploy"
        title={a ? t('admin.deploy.title', { name: a.name }) : t('admin.deploy.titleShort')}
        description={a ? `${a.slug} · ${t('admin.deploy.latest', { version: a.latestVersion })}` : undefined}
        actions={
          a &&
          canManage && (
            <>
              <Button variant="outline" asChild>
                <Link to={`/agents/${id}`}>{t('admin.deploy.backToAgent')}</Link>
              </Button>
              {a.paused ? (
                <Button variant="outline" onClick={() => resume.mutate()} disabled={resume.isPending}>
                  <Play /> {t('admin.deploy.resume')}
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setPauseOpen(true)}>
                  <Pause /> {t('admin.deploy.pause')}
                </Button>
              )}
            </>
          )
        }
      />
      <div className="grid gap-8 p-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Section title={t('admin.deploy.newDeployment')} description={t('admin.deploy.newDeploymentHint')}>
          {versions.isPending && <Skeleton className="h-8 w-48" />}
          {versions.data && published.length === 0 && <p className="text-sm text-muted-foreground">{t('admin.deploy.noPublished')}</p>}
          {published.length > 0 && (
            <Card className="flex flex-col gap-4 p-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t('admin.deploy.version')} htmlFor="dep-version">
                  <NativeSelect id="dep-version" value={version ?? ''} onChange={(e) => setVersion(Number(e.target.value))}>
                    {published.map((v) => (
                      <option key={v.version} value={v.version}>
                        v{v.version}
                        {v.publishedAt ? ` · ${new Date(v.publishedAt).toLocaleDateString()}` : ''}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label={t('admin.deploy.startAt')} htmlFor="dep-start" hint={t('admin.deploy.startAtHint')}>
                  <Input id="dep-start" type="datetime-local" value={startAt} onChange={(e) => setStartAt(e.target.value)} />
                </Field>
              </div>
              <fieldset>
                <legend className="mb-1 text-xs font-medium">{t('admin.deploy.target')}</legend>
                {branches.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('admin.deploy.allBranches')}</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {branches.map((b) => (
                      <label key={b.id} className="flex items-center gap-1 text-sm">
                        <input
                          type="checkbox"
                          checked={selectedBranches.includes(b.id)}
                          onChange={(e) => setSelectedBranches((cur) => (e.target.checked ? [...cur, b.id] : cur.filter((x) => x !== b.id)))}
                        />
                        {b.name}
                      </label>
                    ))}
                    <span className="text-xs text-muted-foreground">{t('admin.deploy.noneMeansAll')}</span>
                  </div>
                )}
              </fieldset>

              <div className="flex items-center justify-between">
                <div className="text-xs font-medium">{t('admin.deploy.checks')}</div>
                <Button size="sm" variant="ghost" onClick={() => void checks.refetch()} disabled={checks.isFetching}>
                  {t('admin.deploy.recheck')}
                </Button>
              </div>
              {checks.isPending && version !== null && <Skeleton className="h-24 w-full" />}
              {checks.data && <DeployChecksList result={checks.data} />}
              <RootError message={deployError ?? undefined} />
              <div>
                <Button onClick={() => deploy.mutate()} disabled={!canManage || !canDeploy(checks.data) || deploy.isPending || checks.isFetching}>
                  <Rocket /> {t('admin.deploy.deploy', { version: version ?? '' })}
                </Button>
              </div>
            </Card>
          )}
        </Section>

        <Section title={t('admin.deploy.activeTitle')}>
          {active ? (
            <Card className="flex items-center justify-between gap-3 p-4" data-testid="active-deployment">
              <div>
                <div className="text-sm font-medium">
                  v{active.version} <Badge variant={statusVariant(active.status)}>{t(`admin.deploymentStatus.${active.status}`)}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">{t('admin.deploy.since', { at: new Date(active.deployedAt).toLocaleString() })}</div>
              </div>
              {canManage && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={rollback.isPending}
                  onClick={() => {
                    if (window.confirm(t('admin.deploy.confirmRollback', { version: active.version }))) rollback.mutate(active.id);
                  }}
                >
                  <RotateCcw /> {t('admin.deploy.rollback')}
                </Button>
              )}
            </Card>
          ) : (
            <p className="text-sm text-muted-foreground">{t('admin.deploy.noActive')}</p>
          )}

          <DataTable head={[t('admin.deploy.version'), t('admin.deploy.status'), t('admin.deploy.deployedAt'), t('admin.deploy.endedAt'), t('admin.deploy.scope')]}>
            {history.length === 0 && <EmptyRow colSpan={5}>{t('admin.deploy.noHistory')}</EmptyRow>}
            {history.map((d) => {
              const scopeBranches = asArray(asRecord(d.scope)['branches']).length;
              return (
                <tr key={d.id}>
                  <td>v{d.version}</td>
                  <td>
                    <Badge variant={statusVariant(d.status)}>{t(`admin.deploymentStatus.${d.status}`)}</Badge>
                  </td>
                  <td className="text-xs">{new Date(d.deployedAt).toLocaleString()}</td>
                  <td className="text-xs">{d.endedAt ? new Date(d.endedAt).toLocaleString() : '—'}</td>
                  <td className="text-xs">{scopeBranches > 0 ? t('admin.deploy.branchCount', { count: scopeBranches }) : t('admin.deploy.all')}</td>
                </tr>
              );
            })}
          </DataTable>
        </Section>
      </div>
      {pauseOpen && <PauseDialog agentId={id} onClose={() => setPauseOpen(false)} onDone={refresh} />}
    </div>
  );
}

function PauseDialog({ agentId, onClose, onDone }: { agentId: string; onClose: () => void; onDone: () => Promise<void> }) {
  const { t } = useTranslation();
  const [cancelRunning, setCancelRunning] = useState(false);
  const pause = useMutation({
    mutationFn: async () =>
      // The OpenAPI document omits the optional PauseRequest body (backend reads it manually); send it untyped.
      unwrap(await api.POST('/v1/agents/{id}/pause', { params: { path: { id: agentId } }, body: { cancelRunning, reason: null } as never })),
    onSuccess: async (res) => {
      toast.success(t('admin.deploy.paused', { count: res.cancelledRuns }));
      await onDone();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{t('admin.deploy.pauseTitle')}</DialogTitle>
        <DialogDescription>{t('admin.deploy.pauseHint')}</DialogDescription>
        <div className="mt-3 flex flex-col gap-3">
          <Switch label={t('admin.deploy.cancelRunning')} checked={cancelRunning} onChange={(e) => setCancelRunning(e.target.checked)} />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              {t('admin.common.cancel')}
            </Button>
            <Button variant={cancelRunning ? 'destructive' : 'default'} onClick={() => pause.mutate()} disabled={pause.isPending}>
              {t('admin.deploy.pause')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
